import type { ReactNode } from 'react';
import { cx } from '../cx';
import { VALUE_TEXT } from './scale';

/**
 * How a chart lays itself out. `auto` (the default) is responsive: every chart is its own size
 * container and switches to its narrow form below the `@md` container width (28rem), wherever it
 * sits, so a chart in a sidebar gets the phone form on a desktop too. `narrow` always uses the phone
 * form (a phone-only page, a tight column, an email); `wide` always uses the full form.
 *
 * The narrow forms keep the same data and the same reading; what moves is the labelling: direct
 * labels that need a side gutter become a key under the chart, worked examples move under the
 * plot, and dense tick rows thin out.
 *
 * A chart that is its own size container must get its width from its parent (a block, a grid cell,
 * a stretched flex item). Something that sizes to its content, like `Ring`, would measure zero, so
 * its `auto` keeps the wide form and only `narrow` switches.
 */
export type ChartLayout = 'auto' | 'narrow' | 'wide';

/** Shown only in the narrow form. Literal class strings, so Tailwind sees every one. */
export const NARROW_ONLY: Record<ChartLayout, string> = { auto: '@md:hidden', narrow: '', wide: 'hidden' };
/** Shown only in the wide form. */
export const WIDE_ONLY: Record<ChartLayout, string> = { auto: '@max-md:hidden', narrow: 'hidden', wide: '' };
/**
 * Blank in the narrow form but still taking its space: for cells of a CSS grid, where `hidden` would
 * drop the cell and shift every cell after it.
 */
export const WIDE_ONLY_KEEP_SPACE: Record<ChartLayout, string> = {
  auto: '@max-md:invisible',
  narrow: 'invisible',
  wide: '',
};

export type LineKeyItem = { id: string; label: ReactNode; story?: boolean; value?: ReactNode };

/** The key the narrow forms put under a line chart in place of labels at the line ends. */
export function LineKey({ items, className }: { items: LineKeyItem[]; className?: string }) {
  return (
    <div className={cx('flex flex-wrap gap-x-4 gap-y-1 type-meta text-jc-muted', className)} aria-hidden="true">
      {items.map((it) => (
        <span key={it.id} className={cx('inline-flex items-center gap-1.5', it.story && 'font-semibold text-jc-ink')}>
          <span className={cx('h-0.5 w-4 rounded-full', it.story ? 'bg-jc-ink' : 'bg-jc-chart-context')} />
          {it.label}
          {it.value ? <span className={VALUE_TEXT}>{it.value}</span> : null}
        </span>
      ))}
    </div>
  );
}
